import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCurator } from "../lib/useCurator";

const links = [
  { to: "/", label: "Home" },
  { to: "/library", label: "The Library" },
  { to: "/curriculums", label: "Personal Curriculums" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, login, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Curator shortcut: only shown when the admin session cookie is still valid
  const curator = useCurator(location.pathname);
  const showCurator = curator && location.pathname !== "/admin";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-md border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" aria-label="Tirthak's Library — home" className="group shrink-0">
          <img
            src="/icon-192.png"
            alt="Tirthak's Library"
            width={40}
            height={40}
            className="w-9 h-9 lg:w-10 lg:h-10 border border-line group-hover:border-crimson transition-colors duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-xs uppercase tracking-[0.2em] link-underline ${
                location.pathname === l.to ? "text-vellum" : "text-vellum-muted hover:text-onaccent"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/my-library"
              data-testid="nav-link-my-library"
              className={`font-mono text-xs uppercase tracking-[0.2em] link-underline ${
                location.pathname === "/my-library" ? "text-vellum" : "text-vellum-muted hover:text-onaccent"
              }`}
            >
              My Account
            </Link>
          )}
          {showCurator && (
            <Link
              to="/admin"
              data-testid="nav-curator-link"
              title="Back to the curator dashboard"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border border-brass/50 text-brass hover:bg-brass hover:text-ink transition-colors"
            >
              <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
            </Link>
          )}
          <div className="w-px h-5 bg-line" />
          {user ? (
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs text-brass truncate max-w-[140px]" data-testid="nav-user-name">
                {user.name || user.email}
              </span>
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted hover:text-crimson transition-colors"
              >
                Sign out
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={login}
              className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors"
            >
              Sign in
            </button>
          )}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden font-mono text-xs uppercase tracking-[0.2em] text-vellum"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ink/95 backdrop-blur-md border-t border-line px-6 py-8 flex flex-col gap-6">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="font-serif text-2xl text-vellum" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          {user && (
            <Link to="/my-library" className="font-serif text-2xl text-vellum" data-testid="nav-mobile-my-library">My Account</Link>
          )}
          {showCurator && (
            <Link to="/admin" data-testid="nav-mobile-curator" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-brass">
              <LayoutDashboard className="w-4 h-4" /> Curator dashboard
            </Link>
          )}
          <div className="pt-2" />
          {user ? (
            <button onClick={logout} className="font-mono text-xs uppercase tracking-[0.2em] text-crimson text-left">Sign out</button>
          ) : (            <button onClick={login} className="font-mono text-xs uppercase tracking-[0.2em] text-crimson text-left" data-testid="nav-mobile-login">Sign in</button>
          )}
        </div>
      )}
    </header>
  );
}
